import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import { GlobalThemeColors } from '~/@types/colors'
import {
  processAlgoliaFilters,
  serializeAlgoliaResults,
} from '~/lib/algolia/serialize-algolia-results'

import { Facets } from '@unlikelystudio/horizon-algolia'
import { Spinner } from '@unlikelystudio/react-abstract-components'
import {
  AlgoliaSearchProvider,
  useAlgoliaQueryContext,
  useAlgoliaSearch,
} from '@unlikelystudio/react-ecommerce-hooks'
import { useIsMountedState } from '@unlikelystudio/react-hooks'

import { useNavBannerContext } from '~/components/Navigation/NavigationBanner'
import FilteredProductsGrid, {
  FilteredProductsGridProps,
} from '~/components/UI/FilteredProductsGrid'
import { FilteredHeaderBlockProps } from '~/components/UI/FilteredProductsGrid/FilteredHeaderBlock'
import FiltersTrigger from '~/components/UI/FiltersTrigger'
import Pagination from '~/components/UI/Pagination'

import {
  getFacetsFiltersFromFilters,
  getFilteredAlgoliaParams,
  getMinMaxValuesFromFacet,
  HITS_PER_PAGE,
  NumericFilters,
  OrderingFilters,
  SortByValues,
  useFilter,
} from '~/providers/FilterProvider'
import {
  LAYOUT,
  useLayoutSwitcherProvider,
} from '~/providers/LayoutSwitcherProvider'
import { usePagination } from '~/providers/PaginationProvider'
import { useStyle } from '~/providers/StyleProvider'

import useLocale from '~/hooks/useLocale'

import deepEqual from '~/utils/deep-equal'
import getAlgoliaProductIndex from '~/utils/get-algolia-product-index'
import { getQueryParams } from '~/utils/get-query-params'

import { CategoryPageData } from '~/data/category-page-data'
import type { AlgoliaInitialSearchResponse } from '~/data/serialize-algolia-initial-search'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type PageWithFiltersProps = CategoryPageData & {
  className?: string
  uid?: string
  defaultIdsToIgnore?: number[]
  children?: JSX.Element | JSX.Element[]
  defaultFilters?: Facets
  customLayout?: boolean
  excludeIds?: string
  filteredHeader?: FilteredHeaderBlockProps
  algoliaProps?: AlgoliaInitialSearchResponse
  defaultIndex?: string
  randomImages?: FilteredProductsGridProps['randomImages']
  withPriority?: boolean
}

const REMOVABLE_EMPTY_KEYS = [
  OrderingFilters.ORDER_BY,
  NumericFilters.PRICE,
  'layout',
]

/**
 * It returns the index name to use for the search query
 * @param sortBy - The sortBy value that the user has selected.
 * @param defaultIndex - The default index to use if the user has not selected a sortBy value.
 * @param locale - The locale of the current user.
 * @returns the indice to use.
 */
function getIndiceFromSortBy(sortBy, defaultIndex, locale) {
  const indexName = getAlgoliaProductIndex()
  // define if use selected default sortBy
  const isDefaultSort =
    (sortBy ?? SortByValues.DEFAULT) === SortByValues.DEFAULT

  // set our default value according to the provided defaultIndex
  const defaultValue = defaultIndex ? defaultIndex : `${locale}_${indexName}`

  // if default selected return default value
  // else return user selection sortBy
  return isDefaultSort ? defaultValue : `${locale}_${indexName}_${sortBy}`
}

const getReducedQueryValuesForFilters =
  (facets) =>
  (cur, [key, value]) => {
    const isDefaultOrderByValue =
      key === OrderingFilters.ORDER_BY && value === SortByValues.DEFAULT
    const isDefaultLayoutValue = key === 'layout' && value === LAYOUT.MEDIUM
    const isDefaultPriceRangeValue =
      key === NumericFilters.PRICE &&
      JSON.stringify(getMinMaxValuesFromFacet(NumericFilters.PRICE, facets)) ===
        value

    if (
      isDefaultOrderByValue ||
      isDefaultLayoutValue ||
      isDefaultPriceRangeValue
    )
      return Object.assign(cur, {
        [key]: [],
      })

    if (!value) return cur

    return Object.assign(cur, {
      [key]: value,
    })
  }

function PageWithFilters({
  children,
  customLayout = false,
  filteredHeader,
  initialFilters,
  defaultIndex,
  randomImages,
  withPriority,
}: PageWithFiltersProps) {
  const [isVisible] = useNavBannerContext()
  const gridStyle = useStyle({ grid: 'base-grid' })
  const router = useRouter()
  const params = useMemo(() => getQueryParams(), [router])
  const locale = useLocale()
  const [_, setQuery] = useAlgoliaQueryContext()
  const filteredParamsFromUrl = getFilteredAlgoliaParams(params ?? {})

  const initialQuery = useRef(_)
  const { data, isSuccess, isFetching } = useAlgoliaSearch()
  const { ref: inViewRef, inView, entry } = useInView({ threshold: 0.2 })

  const isInview = inView || entry?.boundingClientRect?.y <= 0
  // use mounted state to prevent superfluous router push on first render
  const mounted = useIsMountedState()
  const { facets, filters, setFilters, defaultFilters, hitsPerPage } =
    useFilter()
  const { currentPage, routerPush, algoliaPage } = usePagination()
  const { step, setStep } = useLayoutSwitcherProvider()

  const isMounted = useRef(false)
  const facetFilters = useMemo(() => {
    return [
      ...getFacetsFiltersFromFilters(filters),
      ...getFacetsFiltersFromFilters(defaultFilters ?? {}),
    ]
  }, [JSON.stringify(filters ?? {})])

  const sortBy = useMemo(() => {
    return filters?.[OrderingFilters.ORDER_BY] ?? null
  }, [JSON.stringify(filters ?? {})])

  const numericFilters = useMemo(() => {
    const minMaxValues = getMinMaxValuesFromFacet(NumericFilters.PRICE, facets)

    return (
      Object.keys(filters)
        ?.filter(
          (filter) =>
            filters?.[filter] && Object.values(NumericFilters).includes(filter),
        )
        ?.map((filter) => {
          const values = JSON.parse(filters[filter] as string)
          // Avoid rewriting numeric filter if price filter is equal to default
          if (JSON.stringify(values) === JSON.stringify(minMaxValues)) return []
          return values.map(
            (val, i) => `numeric_price ${i === 0 ? `>= ${val}` : `<= ${val}`}`,
          )
        })
        ?.flat()
        ?.join(' AND ') ?? ''
    )
  }, [JSON.stringify(filters ?? {})])

  const getNextState = () => {
    const indice = getIndiceFromSortBy(sortBy, defaultIndex, locale)

    // We need to check here if filters value are different from initial values
    const isFiltering =
      initialQuery.current.index !== indice ||
      JSON.stringify(initialQuery.current.facetFilters) !==
        JSON.stringify(facetFilters)

    const withoutExcludeIds =
      step === LAYOUT.SMALL || (isFiltering && isMounted.current)

    const processedFilters = processAlgoliaFilters([
      numericFilters,
      initialFilters?.newProductsFilter,
      initialFilters?.publishedAtFilters,
      withoutExcludeIds ? '' : initialFilters?.excludeIds,
    ])

    /* Algolia expect an array of arrays for each facet you want to retrieve like [['concerns:concern1', 'concerns:concern2'],['categories:categorie1', 'categories:categorie2']] */
    return {
      ..._,
      page: algoliaPage,
      hitsPerPage: customLayout ? HITS_PER_PAGE.FILTERED : hitsPerPage,
      filters: processedFilters,
      facetFilters,
      index: indice,
    }
  }

  useEffect(() => {
    if (facetFilters === undefined) return

    if (defaultFilters) {
      if (!isMounted.current) {
        isMounted.current = true
        return
      }
    }

    /* Algolia expect an array of arrays for each facet you want to retrieve like [['concerns:concern1', 'concerns:concern2'],['categories:categorie1', 'categories:categorie2']] */
    const nextState = getNextState()
    const shouldChange =
      Object.entries(_).filter(([key, value]) => {
        return JSON.stringify(nextState?.[key]) !== JSON.stringify(value)
      })?.length > 0

    if (!shouldChange) return

    setQuery(nextState)
  }, [filters, facetFilters, hitsPerPage, algoliaPage, currentPage])

  const results = data?.pages?.reduce((acc, page) => {
    return [
      ...serializeAlgoliaResults(acc),
      ...serializeAlgoliaResults(page.hits),
    ]
  }, [])
  const processedQuery = useMemo(() => {
    if (!mounted) return {}
    const newQueryObject =
      Object.entries({
        ...router.query,
        ...(Object.keys(filters)?.length > 0 ? filters : {}),
        layout: step,
      })
        ?.filter(
          ([key, value]) =>
            // remove unwanted parameters from url
            value !== null && key !== 'uid' && key !== 'uids' && key !== 'page',
        )
        ?.reduce(getReducedQueryValuesForFilters(facets), {}) ?? {}

    const newQueryObjectMinusUnusedParams =
      Object.entries(newQueryObject)?.reduce((cur, [key, value]) => {
        const shouldRemoveKey =
          REMOVABLE_EMPTY_KEYS.indexOf(key) !== -1 &&
          Array.isArray(value) &&
          value?.length === 0 &&
          !filteredParamsFromUrl?.[key]

        if (shouldRemoveKey) return cur

        return Object.assign(cur, {
          [key]: value,
        })
      }, {}) ?? {}

    return (
      // The next query parameters are
      // current query parameters + current filters with values + current layout state
      newQueryObjectMinusUnusedParams ?? {}
    )
  }, [JSON.stringify(filters ?? {}), step])

  const hasParams = !!Object.keys(processedQuery)?.length

  // Push to url the updated params from filter providers
  useEffect(() => {
    if (!mounted || !hasParams) return

    // Push to new route if processedQuery is different from current url params
    if (!deepEqual(filteredParamsFromUrl, processedQuery)) {
      routerPush(processedQuery)
    }
  }, [processedQuery])

  useEffect(() => {
    if (params?.layout && params?.layout !== LAYOUT.MEDIUM) {
      setStep(params?.layout as LAYOUT)
    }
    if (!deepEqual(filteredParamsFromUrl, filters)) {
      setFilters(filteredParamsFromUrl)
    }
  }, [])

  return (
    <>
      <section className={cx(css.section, gridStyle)}>
        <FiltersTrigger
          className={cx(css.filtersTrigger, {
            isBannerVisible: isVisible,
            isEnd: isInview && !customLayout,
            customLayout,
          })}
        />
        <div className={cx(css.content)}>
          {isSuccess ? (
            <FilteredProductsGrid
              hasResults={results?.length > 0}
              results={results}
              filteredHeaderBlock={filteredHeader}
              currentPage={currentPage}
              customLayout={customLayout}
              maxPage={data?.pages?.[0]?.nbPages}
              randomImages={randomImages}
              withPriority={withPriority}
            />
          ) : (
            isFetching && (
              <Spinner
                className={css.spinner}
                color={GlobalThemeColors.Black}
              />
            )
          )}
        </div>
        {!customLayout && (
          <FiltersTrigger
            className={cx(css.filtersTriggerBottom, {
              isBannerVisible: isVisible,
              isEnd: isInview && !customLayout,
            })}
          />
        )}
        <div className={cx(css.marker)} ref={inViewRef}></div>
      </section>
      {results?.length > 0 && data?.pages?.[0]?.nbPages > 1 && (
        <Pagination
          className={cx(css.pagination)}
          maxPage={data?.pages?.[0]?.nbPages}
        />
      )}
      {children}
    </>
  )
}

PageWithFilters.defaultProps = {}

export default function PageWithFiltersWrapper({
  children,
  excludeIds,
  algoliaProps,
  ...props
}: PageWithFiltersProps) {
  const { asPath } = useRouter()
  const locale = useLocale()
  const { defaultFilters, hitsPerPage } = useFilter()

  return (
    <AlgoliaSearchProvider
      queryKey={asPath}
      appId={process.env.NEXT_PUBLIC_ALGOLIA_APP_ID}
      apiKey={process.env.NEXT_PUBLIC_ALGOLIA_API_KEY}
      initialSearchProps={{
        hitsPerPage,
        index: `${locale}_${getAlgoliaProductIndex()}`,
        facetFilters: `${getFacetsFiltersFromFilters(defaultFilters)}`,
        filters: processAlgoliaFilters(
          Object.values(props.initialFilters ?? {}),
        ),
        ...algoliaProps?.params,
      }}
      defaultQueryOptions={{
        refetchOnMount: false,
        initialData: {
          ...algoliaProps?.initialData,
          pageParams: [],
        },
      }}>
      <PageWithFilters
        {...props}
        excludeIds={excludeIds}
        algoliaProps={algoliaProps}>
        {children}
      </PageWithFilters>
    </AlgoliaSearchProvider>
  )
}
